export const PROJECT_STATUSES = [
  'IDEA',
  'CONCEPT_DESIGN',
  'IN_DEVELOPMENT',
  'SHELVED',
  'LIVE',
  'ADMINISTRATIVE',
  'OTHER',
] as const;

export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

/**
 * Display order is this array, never the enum's own ordering: Postgres appends
 * new enum values to the end of its sort order, so a stage added mid-pipeline
 * later would sort out of place.
 */
export const STATUS_LABELS: Record<ProjectStatus, string> = {
  IDEA: 'Idea',
  CONCEPT_DESIGN: 'Concept design',
  IN_DEVELOPMENT: 'In development',
  SHELVED: 'Shelved',
  LIVE: 'Live',
  ADMINISTRATIVE: 'Administrative',
  OTHER: 'Other',
};

/**
 * Muted fills so a grid of cards does not turn into a colour chart.
 *
 * Four of the seven used to be the same stone pill at three barely different
 * text weights, which meant Idea, Shelved and Other were indistinguishable at
 * the size these actually render -- a 20px pill halfway down a card. Idea and
 * Other now carry hues of their own; Shelved keeps the neutral, and is the
 * palest of them on purpose, because "put down for now" is the one stage that
 * should recede.
 *
 * The hues left over after the task statuses took sky, violet, amber and
 * emerald: teal reads as early and unformed, rose as the odd-one-out bucket.
 */
export const STATUS_BADGE_CLASSES: Record<ProjectStatus, string> = {
  IDEA: 'bg-teal-100 text-teal-700',
  CONCEPT_DESIGN: 'bg-violet-100 text-violet-700',
  IN_DEVELOPMENT: 'bg-honey-100 text-honey-800',
  SHELVED: 'bg-stone-100 text-stone-500',
  LIVE: 'bg-emerald-100 text-emerald-700',
  ADMINISTRATIVE: 'bg-sky-100 text-sky-700',
  OTHER: 'bg-rose-100 text-rose-700',
};

export function isProjectStatus(value: string): value is ProjectStatus {
  return (PROJECT_STATUSES as readonly string[]).includes(value);
}
